import { computed, nextTick, onMounted, onScopeDispose, ref, watch } from "vue";
import { useHostElement } from "#ce/composables/useHostElement";
import { useFinder } from "#ce/composables/useFinder";
import { resolveFinder } from "#ce/shared/finder-association";
import { i18n } from "#ce/components/SpFinder/i18n";

const keptFields = new Set(["zip", "lat", "lng", "distance", "locationDisplayName", "insurance_plan"]);

/**
 * Binds the server-rendered landing form to the enclosing finder's draft and public commands.
 * An insertion target is an empty server-rendered `data-finder-landing-target` element the app
 * teleports a Vue control into.
 */
export function useFinderLanding(locationControl, catalogsReady) {
  const host = useHostElement();
  const finder = useFinder();
  const settings = computed(() => finder.settings.value);
  const draft = computed(() => finder.landingDraft.value ?? {});
  const locationTarget = ref();
  const featuredTarget = ref();
  let omniSearch;
  let insurance;
  let sections = [];
  let filterContainers = [];
  let removers = [];
  let queuedSubmit;
  const catalogWatchers = new Set();

  function owner() {
    return finder.connected.value ? resolveFinder(host).element : undefined;
  }
  function listen(element, event, handler) {
    element?.addEventListener(event, handler);
    removers.push(() => element?.removeEventListener(event, handler));
  }
  function updateDraft(patch, options) {
    owner()?.updateLandingDraft(patch, options);
  }
  function resetSearch(patch = {}) {
    const cleared = Object.fromEntries(
      Object.keys(draft.value)
        .filter((key) => !keptFields.has(key))
        .map((key) => [key, null]),
    );
    updateDraft({ ...cleared, view: "bio", sort: "best_match", ...patch });
  }
  function waitForCatalogs() {
    return new Promise((resolve) => {
      function settle(ready) {
        if (ready) {
          stop();
          catalogWatchers.delete(stop);
          resolve();
        }
      }
      const stop = watch(catalogsReady, settle);
      catalogWatchers.add(stop);
    });
  }
  function passesValidation() {
    if (settings.value?.landing.requireSkill && !omniSearch?.hasSelection) {
      omniSearch?.setCustomValidity?.(i18n.global.t("landing.skill_invalid"));
      omniSearch?.reportValidity?.();
      return false;
    }
    return locationControl.value?.reportValidity() !== false;
  }
  function cancelQueuedSubmit() {
    queuedSubmit = undefined;
  }
  function cancelQueuedSubmitOnLeave(route) {
    if (route !== queuedSubmit?.route) {
      cancelQueuedSubmit();
    }
  }
  async function submitWhenReady({ validate }) {
    if (!catalogsReady.value) {
      if (queuedSubmit) {
        queuedSubmit.validate = validate;
        return;
      }
      const entry = { validate, route: finder.route.value };
      queuedSubmit = entry;
      await waitForCatalogs();
      await nextTick();
      if (queuedSubmit !== entry || finder.route.value !== entry.route) {
        return;
      }
      queuedSubmit = undefined;
      ({ validate } = entry);
    }
    if (validate && !passesValidation()) {
      return;
    }
    owner()?.submitLandingSearch();
  }
  function submitSearch() {
    return submitWhenReady({ validate: true });
  }
  function handleSubmit(event) {
    event.preventDefault();
    submitSearch();
  }
  function handleClear() {
    resetSearch();
  }
  function handleText({ detail }) {
    if (settings.value?.landing.textSearchEnabled) {
      resetSearch({ query: detail?.[0]?.trim() || null });
    }
  }
  function handleSelect({ detail }) {
    const payload = detail?.[0];
    if (!payload) {
      return;
    }
    omniSearch?.setCustomValidity?.("");
    let filters;
    if (payload.filters) {
      filters = payload.filters;
    } else if (payload.value) {
      filters = { [payload.filter || "skill"]: payload.value };
      if (settings.value?.landing.skillSetsNewPatient && payload.referralRequired === false) {
        filters.new_patient = true;
      }
    } else {
      filters = { query: payload.query || payload.title };
    }
    resetSearch(filters);
    if (payload.url || payload.type === "suggested-filter") {
      owner()?.navigate({
        url: payload.url,
        suggestedFilters: payload.type === "suggested-filter" ? filters : undefined,
        query: payload.type === "suggested-filter" ? payload.query : undefined,
      });
    }
    if (!payload.url && payload.navigateOnSelect) {
      submitWhenReady({ validate: false });
    }
  }
  function handleInsurance(event) {
    if (!event.detail) {
      return;
    }
    event.stopPropagation();
    updateDraft({ insurance_plan: event.detail[0] ?? null });
  }
  function featuredSearch(filters) {
    resetSearch(filters);
    cancelQueuedSubmit();
    owner()?.submitLandingSearch();
  }
  function openOmniSearch() {
    omniSearch?.openSearch?.();
  }
  function focusSearch(event) {
    event?.preventDefault();
    host.scrollIntoView({ block: "start", behavior: "smooth" });
    // Wait for the embedded finder's parent click relay before opening the menu.
    requestAnimationFrame(openOmniSearch);
  }
  function handleReset() {
    cancelQueuedSubmit();
    locationControl.value?.resetValidation();
    const requiredFiltersLocked = settings.value?.location.filterOptions?.required_filters_modal;
    resetSearch(requiredFiltersLocked ? {} : { insurance_plan: null });
    omniSearch?.clear?.();
    if (!requiredFiltersLocked) {
      locationControl.value?.clearQuery();
      updateDraft({ zip: null, lat: null, lng: null, distance: null, locationDisplayName: null });
    }
  }
  function syncServerControls() {
    if (insurance) {
      insurance.value = draft.value.insurance_plan ?? "";
    }
    for (const section of sections) {
      const choices = settings.value?.landing.sections;
      if (choices) {
        section.hidden = !section.dataset.doctorFinderLandingPageSection.split(/\s+/).some((name) => choices[name]);
      }
    }
    for (const container of filterContainers) {
      const filters = settings.value?.landing.filters;
      const enabled =
        settings.value?.landing.showFilters &&
        filters !== false &&
        (!Array.isArray(filters) || filters.includes(container.dataset.filterName));
      container.hidden = !enabled;
    }
    locationTarget.value?.parentElement?.classList.toggle("active", Boolean(draft.value.locationDisplayName));
  }
  function cacheTargets() {
    removers.splice(0).forEach((remove) => remove());
    locationTarget.value = host.querySelector('[data-finder-landing-target="location"]');
    featuredTarget.value = host.querySelector('[data-finder-landing-target="featured"]');
    omniSearch = host.querySelector('[ref="omni-search"]');
    insurance = host.querySelector('[name="insurance_plan"]');
    sections = [...host.querySelectorAll("[data-doctor-finder-landing-page-section]")];
    filterContainers = [...host.querySelectorAll("[data-filter-name]")];
    listen(omniSearch, "select", handleSelect);
    listen(omniSearch, "clear", handleClear);
    listen(omniSearch, "text-change", handleText);
    listen(omniSearch, "submit", handleSubmit);
    listen(insurance, "change", handleInsurance);
    listen(host.querySelector(".doctor-finder-landing-search-submit"), "click", handleSubmit);
    host
      .querySelectorAll('[data-doctor-finder-behaviour~="focus-landing-page-search"]')
      .forEach((element) => listen(element, "click", focusSearch));
    listen(host, "reset", handleReset);
    syncServerControls();
  }
  onMounted(cacheTargets);
  watch(settings, cacheTargets);
  watch(draft, syncServerControls);
  watch(finder.route, cancelQueuedSubmitOnLeave);
  function disposeListeners() {
    catalogWatchers.forEach((stop) => stop());
    catalogWatchers.clear();
    removers.splice(0).forEach((remove) => remove());
  }
  onScopeDispose(disposeListeners);
  return {
    settings,
    draft,
    draftTransientLocation: finder.landingDraftTransientLocation,
    route: finder.route,
    locationTarget,
    featuredTarget,
    updateLocation: updateDraft,
    featuredSearch,
    focusSearch,
  };
}
